<template>
  <a-modal
    v-model:open="open"
    class="p-0"
    :closable="false"
    :body-style="{ padding: '0px' }"
    destroy-on-close
    @cancel="close"
  >
    <div class="flex flex-col gap-y-3">
      <div>
        <a-input
          v-model:value="search"
          class="border border-cyan-600 border-solid"
          placeholder="输入名称或项目代号进行过滤"
          size="large"
          allow-clear
        >
          <template #prefix>
            <search-outlined />
          </template>
        </a-input>
      </div>
      <div class="max-h-120 select-none overflow-y-auto">
        <div v-for="group in groups" :key="group.key">
          <div class="my-4 text-sm font-bold text-slate-600">
            {{ group.title }}
          </div>
          <div class="divide-x-0 divide-y-1 divide-gray-300 divide-solid">
            <template v-if="group.key === GroupKey.Mine && isNotJoinAnyProject">
              <a-empty>
                <template #description>
                  <span class="text-base font-bold text-yellow-700">您暂未加入任何项目/产品</span>
                </template>
              </a-empty>
            </template>
            <template v-else-if="!group.projects.length">
              <a-empty>
                <template #description>
                  <span class="text-base font-bold text-yellow-700">未找到匹配项目/产品名称</span>
                </template>
              </a-empty>
            </template>
            <template v-else>
              <div
                v-for="project in group.projects"
                :key="project.id"
                class="flex flex-row items-center justify-between px-3 py-3 text-base hover:bg-gray-100 hover:text-cyan-800"
                :style="{
                  backgroundColor: projectSticky[project.id] ? '#EDF1FA' : '',
                }"
                :class="shouldDisabled(project) ? 'text-gray-400 hover:text-gray-400' : 'text-black'"
              >
                <div class="flex items-center gap-x-3">
                  <div class="flex items-center">
                    <icon-vue
                      v-if="!projectSticky[project.id]"
                      icon="typcn:pin-outline"
                      class="cursor-pointer text-xl"
                      @click="setProjectSticky(project.id)"
                    />
                    <icon-vue
                      v-if="projectSticky[project.id]"
                      icon="lucide:pin-off"
                      class="cursor-pointer text-xl"
                      @click="setProjectSticky(project.id)"
                    />
                  </div>
                  <icon-vue icon="fluent:share-screen-person-p-16-filled" class="text-3xl text-blue-500" />
                  <span>{{ project.name }}</span>
                  <span v-if="project.code" class="text-gray-400">
                    {{ project.code }}
                  </span>
                </div>
                <a-button
                  class="flex items-center gap-x-0.5 text-sm"
                  type="link"
                  :disabled="shouldDisabled(project)"
                  @click="entryProject(project)"
                >
                  <span>进入</span>
                  <icon-vue icon="mingcute:right-fill" />
                </a-button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="text-gray">点击“进入”，跳转至【项目/产品】空间。如无，联系【项目/产品】管理员</div>
    </template>
  </a-modal>
</template>
<script setup lang="ts">
import { useResidentStore, useUserStore } from '@/store';
import { RoleType, type SysProject } from '@/types/common';
import { PermRoleKey } from '@/types/permission';
import storage from '@/utils/storage';
import { SearchOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { Button as AButton, Input as AInput, Modal as AModal } from 'ant-design-vue';
import { computed, onMounted, ref } from 'vue';
import type { CommonProjectSwitchOptions } from './index';

const props = defineProps<{
  onClose: () => void;
}>();
const userStore = useUserStore();
const residentStore = useResidentStore();
const open = ref(true);
const search = ref('');
const disabled = ref<number[]>([]);
const projectEntryCallback = ref<(p: SysProject) => void>();
const projectSticky = ref<{ [key: string]: boolean }>({});

enum GroupKey {
  Mine = 'mine',
  Other = 'other',
  All = 'all',
}

/**
 * @description 当前用户是否为项目类型
 */
const isProjectType = computed(() => {
  return userStore.isRoleType(RoleType.Project);
});

/**
 * @description 是否未加入任何项目
 */
const isNotJoinAnyProject = computed(() => {
  return isProjectType.value && !residentStore.userSysProjects.length;
});

/**
 * @description 项目列表
 */
const groups = computed(() => {
  if (!userStore.current) {
    return [];
  }
  const currentRoleId = userStore.current.roles[0].id;
  if (currentRoleId === PermRoleKey.TestOd || currentRoleId === PermRoleKey.DevOd) {
    // 外包角色（测试 OD / 研发 OD）只看"我的项目/产品"，不暴露全量项目
    return [
      {
        title: '我的项目/产品',
        key: GroupKey.Mine,
        projects: stickySort(filterProjectsBySearch(residentStore.userSysProjects)),
      },
    ];
  }
  if (isProjectType.value) {
    return [
      {
        title: '我的项目/产品',
        key: GroupKey.Mine,
        projects: stickySort(filterProjectsBySearch(residentStore.userSysProjects)),
      },
      {
        title: '其他项目/产品',
        key: GroupKey.Other,
        projects: stickySort(filterProjectsBySearch(residentStore.nonUserSysProject)),
      },
    ];
  }
  return [
    {
      title: '所有项目/产品',
      key: GroupKey.All,
      projects: stickySort(filterProjectsBySearch(residentStore.allSysProject)),
    },
  ];
});

/**
 * @description 根据搜索过滤项目
 */
function filterProjectsBySearch(ps: SysProject[]) {
  return ps.filter((p) => {
    return (
      p.name.toLowerCase().includes(search.value.toLowerCase()) || // 忽略大小写检索名称
      p.code?.toLowerCase().includes(search.value.toLowerCase()) // 忽略大小写检索项目代码
    );
  });
}

/**
 * @description 是否禁用 project
 */
function shouldDisabled(p: { id: number }) {
  return disabled.value.includes(p.id);
}

/**
 * @description 点击进入项目
 */
function entryProject(p: any) {
  if (!projectEntryCallback.value) {
    console.warn('[ProjectSwitch].onProjectEntry no passed in');
    return;
  }
  projectEntryCallback.value(p);
  close();
}

/**
 * @description
 */
function show(opt: CommonProjectSwitchOptions) {
  open.value = true;
  disabled.value = opt.disabled ? (Array.isArray(opt.disabled) ? opt.disabled : [opt.disabled]) : [];
  projectEntryCallback.value = opt.onProjectEntry;
}

/**
 * @description
 */
function close() {
  open.value = false;
  props.onClose();
}

/**
 * @description 置顶排序
 */
const stickySort = (projectList: SysProject[]) => {
  const stickyProjects = projectList.filter((p) => projectSticky.value[p.id]);
  const normalProjects = projectList.filter((p) => !projectSticky.value[p.id]);
  return stickyProjects.concat(normalProjects);
};

/**
 * @description 初始化置顶项目
 */
const initProjectSticky = () => {
  const projectStickyCache = JSON.parse(storage.get('projectSticky'));
  if (!projectStickyCache) {
    return;
  }
  if (Array.isArray(projectStickyCache)) {
    for (let item of projectStickyCache) {
      projectSticky.value[item] = true;
    }
  }
};

/**
 * @description 设置项目置顶
 */
const setProjectSticky = (id: number) => {
  if (projectSticky.value[id]) {
    delete projectSticky.value[id];
  } else {
    projectSticky.value[id] = true;
  }
  storage.set('projectSticky', Object.keys(projectSticky.value));
};

onMounted(() => {
  residentStore.getAllSysProject();
  residentStore.getUserSysProject();
  initProjectSticky();
});

defineExpose({
  show,
});
</script>
